import { useState, useEffect, useCallback } from 'react';
import { TRIP_TABS, TAB_CONFIG, getTabIndex, isValidTabKey, TripTabKey } from '../Utils/tabNavigation';

export interface TabConfig {
  label: string;
  key: TripTabKey;
  index: number;
}

export const useTabNavigation = (defaultTabKey: TripTabKey = TRIP_TABS.EXPLORE) => {
  const [activeTabIndex, setActiveTabIndex] = useState(() => getTabIndex(defaultTabKey));

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTabIndex(newValue);
    
    // Update URL hash for better navigation and bookmarking
    const tabKey = TAB_CONFIG[newValue]?.key;
    if (tabKey) {
      window.location.hash = `#${tabKey}`;
    }
  }, []);

  // Handle URL hash changes for direct tab navigation
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && isValidTabKey(hash)) {
      const tabIndex = getTabIndex(hash);
      if (tabIndex !== -1) {
        setActiveTabIndex(tabIndex);
      }
    } else if (!hash) {
      // Reset to default tab when hash is empty
      setActiveTabIndex(getTabIndex(defaultTabKey));
    }
  }, [defaultTabKey]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && isValidTabKey(hash)) {
        const tabIndex = getTabIndex(hash);
        if (tabIndex !== -1) {
          setActiveTabIndex(tabIndex);
        }
      } else if (!hash) {
        // Reset to default tab when hash is cleared
        setActiveTabIndex(getTabIndex(defaultTabKey));
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [defaultTabKey]);

  const goToTab = useCallback((tabKey: TripTabKey) => {
    const tabIndex = getTabIndex(tabKey);
    if (tabIndex !== -1) {
      setActiveTabIndex(tabIndex);
      window.location.hash = `#${tabKey}`;
    }
  }, []);

  const getCurrentTab = useCallback(() => {
    return TAB_CONFIG[activeTabIndex];
  }, [activeTabIndex]);

  const isActiveTab = useCallback((tabKey: TripTabKey) => {
    return TAB_CONFIG[activeTabIndex]?.key === tabKey;
  }, [activeTabIndex]);

  return {
    activeTabIndex,
    handleTabChange,
    goToTab,
    getCurrentTab,
    isActiveTab,
    tabs: TAB_CONFIG,
  };
};
